/**
 * Welltrainer. Data Layer Helper para React App
 *
 * Uso:
 *   import { rastrear } from '@/lib/services/tracking-service';
 *   rastrear.cadastroConcluido({ metodo: 'email', usuario: { email, firstName, ... } });
 *
 * Todos os eventos seguem a nomenclatura PT-BR definida no design de rastreamento.
 * O dataLayer e inicializado automaticamente se ainda nao existir.
 *
 * Cada evento gera um event_id UUID automatico para dedup Pixel x CAPI.
 * gclid capturado e persistido por 90 dias em localStorage.
 */

import { buildUserDataForMeta, type UserMetaInput, type UserDataMeta } from '@/lib/utils/user-data-meta';
import { ehAlunoNesteAparelho } from '@/lib/utils/tracking-routes';
import { avisarEventoDaMeta, type EventoDaMetaNoApp } from '@/lib/utils/native-bridge';
import { fbcDeVerdade, fbclidDeVerdade } from '@/lib/utm';

// Declaracao global para TypeScript
declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
    fbq?: (...args: any[]) => void;
    gtag?: (...args: any[]) => void;
    /** Pixel do ChatGPT (OpenAI) — stub criado em `wt-tracking-scripts.tsx` */
    oaiq?: (...args: any[]) => void;
  }
}

// Garante que o dataLayer existe
if (typeof window !== 'undefined' && !window.dataLayer) {
  window.dataLayer = [];
}

// =============================================================================
// gclid capture e persistencia (90 dias)
// =============================================================================

const GCLID_KEY = 'welltrainer_gclid';
const GCLID_TTL_MS = 90 * 24 * 60 * 60 * 1000; // 90 dias

interface StoredGclid {
  value: string;
  ts: number;
}

/**
 * Identificadores de clique do Google Ads alem do gclid. Em iOS 14.5+ o Google
 * nao envia gclid: envia gbraid (web -> app) ou wbraid (app -> web).
 * gad_source / gad_campaignid sao "session attributes" (atribuicao reserva).
 */
const GOOGLE_CLICK_KEYS = ['gclid', 'gbraid', 'wbraid'] as const;
const GOOGLE_SESSION_KEYS = ['gad_source', 'gad_campaignid'] as const;
type GoogleClickKey = (typeof GOOGLE_CLICK_KEYS)[number];

function storageKeyGoogle(key: string): string {
  return key === 'gclid' ? GCLID_KEY : `welltrainer_${key}`;
}

/**
 * Captura gclid / gbraid / wbraid / gad_* da URL atual, persiste em
 * localStorage por 90 dias e em cookie `wt_<chave>` no dominio raiz (mesmo
 * padrao do fbclid: localStorage nao cruza subdominios, cookie sim).
 * Chamar no mount do layout raiz.
 */
export function captureGclid(): void {
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    const agora = Date.now();
    let gclidCapturado: string | null = null;

    for (const key of [...GOOGLE_CLICK_KEYS, ...GOOGLE_SESSION_KEYS]) {
      const valor = url.searchParams.get(key);
      if (!valor) continue;
      const stored: StoredGclid = { value: valor, ts: agora };
      localStorage.setItem(storageKeyGoogle(key), JSON.stringify(stored));
      document.cookie = `wt_${key}=${encodeURIComponent(valor)}; path=/; max-age=${COOKIE_MAX_AGE_S}; SameSite=Lax${cookieDomainAttr()}`;
      if (key === 'gclid') gclidCapturado = valor;
    }

    if (!gclidCapturado) return;
    // Push para o dataLayer para o GTM Conversion Linker pegar
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'gclid_available', gclid: gclidCapturado });
  } catch {
    // ignora
  }
}

function lerGoogleParam(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(storageKeyGoogle(key));
    if (raw) {
      const stored: StoredGclid = JSON.parse(raw);
      if (Date.now() - stored.ts > GCLID_TTL_MS) {
        localStorage.removeItem(storageKeyGoogle(key));
      } else if (stored.value) {
        return stored.value;
      }
    }
  } catch {
    // segue para o fallback de cookie
  }
  return lerCookie(`wt_${key}`);
}

/**
 * Le gclid persistido (se ainda dentro do TTL de 90 dias).
 * Fallback: cookie wt_gclid do dominio raiz (gravado pela landing).
 */
export function getStoredGclid(): string | null {
  return lerGoogleParam('gclid');
}

/**
 * Identificadores de atribuicao do Google Ads persistidos (URL, localStorage
 * ou cookies wt_* do dominio raiz), prontos para ir no cadastro.
 */
export function getGoogleAdsAttribution(): {
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  gad_source?: string;
  gad_campaignid?: string;
} {
  const out: Record<GoogleClickKey | 'gad_source' | 'gad_campaignid', string | undefined> = {
    gclid: undefined,
    gbraid: undefined,
    wbraid: undefined,
    gad_source: undefined,
    gad_campaignid: undefined,
  };
  for (const key of [...GOOGLE_CLICK_KEYS, ...GOOGLE_SESSION_KEYS]) {
    out[key] = lerGoogleParam(key) || undefined;
  }
  return out;
}

/**
 * client_id do GA4 a partir do cookie _ga ("GA1.1.1234567890.1700000000" ->
 * "1234567890.1700000000"). Permite ao backend enviar o purchase ao GA4 na
 * sessao certa (Measurement Protocol).
 */
export function getGaClientId(): string | null {
  const ga = lerCookie('_ga');
  if (!ga) return null;
  const partes = ga.split('.');
  if (partes.length < 4) return null;
  return `${partes[partes.length - 2]}.${partes[partes.length - 1]}`;
}

/**
 * Estado do Consent Mode para fins de anuncios (ad_user_data /
 * ad_personalization), gravado pela landing no cookie wt_consent
 * ("granted" | "denied") quando o visitante responde ao aviso. Sem cookie,
 * assume "granted" (base legal LGPD declarada no aviso de privacidade; o
 * Google nao exige Consent Mode no Brasil).
 */
export function getConsentState(): { ad_user_data: string | null; ad_personalization: string | null } {
  const cookie = (lerCookie('wt_consent') || '').toLowerCase();
  if (cookie === 'granted' || cookie === 'denied') {
    return { ad_user_data: cookie, ad_personalization: cookie };
  }
  return { ad_user_data: 'granted', ad_personalization: 'granted' };
}

// =============================================================================
// fbclid capture e persistencia (90 dias) + cookie _fbc
// =============================================================================

const FBCLID_KEY = 'welltrainer_fbclid';
const FBCLID_TTL_MS = 90 * 24 * 60 * 60 * 1000; // 90 dias
const FBCLID_COOKIE = 'wt_fbclid';
const COOKIE_MAX_AGE_S = 90 * 24 * 60 * 60; // 90 dias

interface StoredFbclid {
  value: string;
  ts: number;
}

/**
 * Atributo `domain` para cookies de tracking. localStorage NAO cruza
 * subdominios, um fbclid capturado em welltrainer.com.br se perderia no
 * redirect para app.welltrainer.com.br. Cookie com domain=.welltrainer.com.br
 * e compartilhado entre todos os subdominios (mesmo comportamento dos
 * cookies _fbp/_fbc do Meta Pixel).
 */
function cookieDomainAttr(): string {
  if (typeof window === 'undefined') return '';
  const host = window.location.hostname;
  if (host === 'welltrainer.com.br' || host.endsWith('.welltrainer.com.br')) {
    return '; domain=.welltrainer.com.br';
  }
  return ''; // localhost / preview: cookie host-only
}

function lerCookie(nome: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${nome}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Captura fbclid da URL atual, persiste em localStorage por 90 dias,
 * e cria o cookie _fbc no formato Meta: fb.1.{timestamp}.{fbclid}.
 * Chamar no mount do layout raiz (junto com captureGclid).
 */
export function captureFbclid(): void {
  if (typeof window === 'undefined') return;
  try {
    esquecerFbclidDaUtmify();
    const url = new URL(window.location.href);
    /* SÓ O FBCLID DA URL. O sufixo depois do `jLj` no utm_source era tirado
       daqui como "o fbclid que a UTMify colou", e é o id de sessão dela: ele
       ia para o aparelho, para o `wt_fbclid` e para o `_fbc` do Meta, e a
       captura de UTM deduzia `facebook / cpc` dele. Ver `fbclidDeVerdade`. */
    const fbclid = fbclidDeVerdade(url.searchParams.get('fbclid'));

    if (!fbclid) return;

    // Persistir em localStorage (90 dias)
    const stored: StoredFbclid = { value: fbclid, ts: Date.now() };
    localStorage.setItem(FBCLID_KEY, JSON.stringify(stored));

    // Cookie proprio de fbclid no dominio raiz, sobrevive ao redirect
    // welltrainer.com.br -> app.welltrainer.com.br (localStorage nao cruza
    // subdominios).
    document.cookie = `${FBCLID_COOKIE}=${encodeURIComponent(fbclid)}; path=/; max-age=${COOKIE_MAX_AGE_S}; SameSite=Lax${cookieDomainAttr()}`;

    // Criar cookie _fbc no formato Meta CAPI (timestamp em MILISSEGUNDOS),
    // tambem no dominio raiz para valer em todos os subdominios.
    const timestamp = Date.now();
    const fbcValue = `fb.1.${timestamp}.${fbclid}`;
    document.cookie = `_fbc=${fbcValue}; path=/; max-age=${COOKIE_MAX_AGE_S}; SameSite=Lax${cookieDomainAttr()}`;

    // Push pro dataLayer (GTM/Meta Pixel)
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'fbclid_available', fbclid, fbc: fbcValue });
  } catch {
    // ignora
  }
}

/**
 * Apaga o que a versão antiga guardou com o id da UTMify (até 27/10/2026): o
 * fbclid no aparelho, o `wt_fbclid` e o `_fbc` montado em cima dele. Os três
 * vivem 90 dias, e iam para o pixel e para o servidor como clique do Meta.
 */
function esquecerFbclidDaUtmify(): void {
  try {
    const raw = localStorage.getItem(FBCLID_KEY);
    if (raw) {
      const stored: StoredFbclid = JSON.parse(raw);
      if (!fbclidDeVerdade(stored?.value)) localStorage.removeItem(FBCLID_KEY);
    }
  } catch {
    // ignora
  }
  try {
    const doCookie = lerCookie(FBCLID_COOKIE);
    if (doCookie && !fbclidDeVerdade(doCookie)) {
      document.cookie = `${FBCLID_COOKIE}=; path=/; max-age=0; SameSite=Lax${cookieDomainAttr()}`;
    }
    const fbc = lerCookie('_fbc');
    if (fbc && !fbcDeVerdade(fbc)) {
      document.cookie = `_fbc=; path=/; max-age=0; SameSite=Lax${cookieDomainAttr()}`;
    }
  } catch {
    // ignora
  }
}

/**
 * Le fbclid persistido (se ainda dentro do TTL de 90 dias).
 * Fallback: cookie wt_fbclid do dominio raiz (setado em qualquer subdominio).
 * O id da UTMify que a versão antiga guardava não sai daqui (`fbclidDeVerdade`).
 */
export function getStoredFbclid(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(FBCLID_KEY);
    if (raw) {
      const stored: StoredFbclid = JSON.parse(raw);
      if (Date.now() - stored.ts > FBCLID_TTL_MS) {
        localStorage.removeItem(FBCLID_KEY);
      } else {
        const valor = fbclidDeVerdade(stored.value);
        if (valor) return valor;
      }
    }
  } catch {
    // segue para o fallback de cookie
  }
  return fbclidDeVerdade(lerCookie(FBCLID_COOKIE)) ?? null;
}

/**
 * Le o cookie _fbp do Meta Pixel (se existir).
 */
export function getFbp(): string | null {
  return lerCookie('_fbp');
}

/**
 * Le o cookie _fbc do Meta Pixel. Se ausente mas houver fbclid persistido
 * (localStorage ou cookie wt_fbclid), constroi fbc = fb.1.{Date.now()}.{fbclid}
 *, timestamp em MILISSEGUNDOS, formato oficial Meta CAPI, e grava o cookie
 * para leituras futuras.
 */
export function getFbc(): string | null {
  const cookie = fbcDeVerdade(lerCookie('_fbc'));
  if (cookie) return cookie;
  const fbclid = getStoredFbclid();
  if (!fbclid) return null;
  const fbcValue = `fb.1.${Date.now()}.${fbclid}`;
  try {
    document.cookie = `_fbc=${fbcValue}; path=/; max-age=${COOKIE_MAX_AGE_S}; SameSite=Lax${cookieDomainAttr()}`;
  } catch {
    // best-effort, o valor construido ainda e retornado
  }
  return fbcValue;
}

// =============================================================================
// Route tracker init (SPA page_view)
// =============================================================================

let routeTrackerLastPath: string | null = null;

/**
 * Dispara pagina_vista se a rota mudou desde a ultima chamada.
 * Chamar em useEffect com dependencia [pathname] no layout raiz.
 */
export function initRouteTracker(): void {
  if (typeof window === 'undefined') return;
  const path = window.location.pathname;
  if (path === routeTrackerLastPath) return;
  routeTrackerLastPath = path;
  pushEvento('pagina_vista', {
    event_id: gerarEventId(),
    pagina: path,
    titulo_pagina: document.title,
    url_completa: window.location.href,
  });
}

// =============================================================================
// User data / user properties (enhanced match + GA4 user properties)
// =============================================================================

export interface SetUserDataInput {
  email?: string | null;
  phone?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  /** id interno do usuario, sera hashado como external_id */
  externalId?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: string | null;
  birthday?: string | null;
  gender?: string | null;
}

export interface UserProperties {
  tipo_usuario?: 'PERSONAL' | 'ALUNO' | 'ADMIN';
  plano_ativo?: string; // 'gratis' | 'solo' | 'pro' | 'escala'
  dias_desde_cadastro?: number;
  numero_alunos?: number;
  numero_planos_criados?: number;
  numero_treinos_concluidos?: number;
  [key: string]: unknown;
}

/**
 * Publica user_data hashado (SHA-256) no dataLayer para enhanced match Meta/Google.
 * Chamar apos login/refresh do usuario.
 */
export async function setUserData(input: SetUserDataInput): Promise<void> {
  if (typeof window === 'undefined') return;
  /* Dado pessoal de aluno não vai para o enhanced match: ele não é lead nosso,
     e o e-mail e o telefone dele não têm por que chegar ao Meta. */
  if (ehAlunoNesteAparelho()) return;
  const meta: UserMetaInput = {
    email: input.email,
    phone: input.phone,
    firstName: input.firstName,
    lastName: input.lastName,
    id: input.externalId,
    city: input.city,
    state: input.state,
    zip: input.zip,
    country: input.country || 'br',
    birthday: input.birthday,
    gender: input.gender,
  };
  const user_data = (await buildUserDataSafe(meta)) as UserDataMeta | undefined;
  if (!user_data || Object.keys(user_data).length === 0) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'set_user_data',
    user_data,
  });
}

/** A origem desta conta, do jeito que a API devolve em `/utm/minha-atribuicao`. */
export interface AtribuicaoDoServidor {
  tem: boolean;
  metodo?: string;
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_content?: string | null;
  utm_term?: string | null;
  fbc?: string | null;
  fbp?: string | null;
  fbclid?: string | null;
  gclid?: string | null;
  gbraid?: string | null;
  wbraid?: string | null;
}

/**
 * Publica no dataLayer a origem que o SERVIDOR descobriu, ANTES do evento de
 * conversão.
 *
 * É a ponte que faltava entre as duas metades do rastreamento. Quem descobre de
 * onde veio quem instalou o app é a API (ela casa o IP do clique no `/go` com o
 * do cadastro); quem dispara as tags do Meta e do Google é o navegador, que
 * chega ao `/cadastro-concluido` sem nenhum parâmetro na URL, porque a pessoa
 * veio da loja de aplicativos. Sem isto, a conversão sai como "direto" mesmo
 * quando o servidor sabe que ela veio de uma campanha.
 *
 * O push vem primeiro de propósito: no GTM, uma variável de camada de dados
 * guarda o último valor publicado, então tudo que é empurrado aqui fica
 * disponível para as tags que dispararem depois, no mesmo carregamento.
 */
export function definirAtribuicao(atribuicao: AtribuicaoDoServidor): void {
  if (typeof window === 'undefined') return;
  if (!atribuicao?.tem) return;
  if (ehAlunoNesteAparelho()) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'atribuicao_conhecida',
    atribuicao_metodo: atribuicao.metodo,
    /* Nomes achatados, e não um objeto: variável de camada de dados do GTM lê
       chave por chave, e `atribuicao.utm_source` obrigaria uma variável de
       caminho para cada campo. */
    atribuicao_utm_source: atribuicao.utm_source || undefined,
    atribuicao_utm_medium: atribuicao.utm_medium || undefined,
    atribuicao_utm_campaign: atribuicao.utm_campaign || undefined,
    atribuicao_utm_content: atribuicao.utm_content || undefined,
    atribuicao_utm_term: atribuicao.utm_term || undefined,
    atribuicao_fbc: atribuicao.fbc || undefined,
    atribuicao_fbp: atribuicao.fbp || undefined,
    atribuicao_fbclid: atribuicao.fbclid || undefined,
    atribuicao_gclid: atribuicao.gclid || undefined,
    atribuicao_gbraid: atribuicao.gbraid || undefined,
    atribuicao_wbraid: atribuicao.wbraid || undefined,
  });
}

/**
 * Publica user properties (GA4) no dataLayer.
 * Chamar apos login e quando os valores mudam (ex: novo aluno cadastrado).
 */
export function setUserProperties(props: UserProperties): void {
  if (typeof window === 'undefined') return;
  if (ehAlunoNesteAparelho()) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'user_properties_set',
    user_properties: props,
  });
}

/**
 * Helper: dispara login + setUserData + setUserProperties com o que sabemos do
 * user armazenado. Best-effort, campos faltantes sao omitidos.
 */
export async function fireAuthTracking(
  metodo: 'email' | 'google' | 'apple',
  user: {
    id?: string;
    email?: string;
    phoneNumber?: string;
    firstName?: string;
    lastName?: string;
    birthday?: string;
    type?: string;
    createdAt?: string;
  },
  extras?: Partial<UserProperties>,
): Promise<void> {
  /*
   * O LOGIN DO ALUNO NÃO É EVENTO NOSSO.
   *
   * Esta função é chamada nos três logins (e-mail, Google, Apple) e era o maior
   * vazamento do app: todo aluno que entrava disparava `login` e publicava o
   * `user_data` dele (e-mail e telefone hasheados) para o enhanced match do
   * Meta. O aluno entra muitas vezes por semana, e nenhuma delas é aquisição:
   * quem decide assinar é o personal.
   *
   * A pergunta é feita pelo `user` recebido, e não pelo que está guardado no
   * aparelho, porque aqui o tipo já veio do servidor nesta resposta. O servidor
   * diz `ALUNO`, o app guarda `TRAINEE`: os dois entram.
   */
  if (user.type === 'TRAINEE' || user.type === 'ALUNO') return;

  rastrear.login({ metodo });

  await setUserData({
    email: user.email,
    phone: user.phoneNumber,
    firstName: user.firstName,
    lastName: user.lastName,
    externalId: user.id,
    birthday: user.birthday,
    country: 'br',
  });

  const tipo_usuario = user.type === 'TRAINEE'
    ? 'ALUNO'
    : (user.type as UserProperties['tipo_usuario']) || undefined;

  let dias_desde_cadastro: number | undefined;
  if (user.createdAt) {
    const diff = Date.now() - new Date(user.createdAt).getTime();
    if (!Number.isNaN(diff) && diff > 0) {
      dias_desde_cadastro = Math.floor(diff / (24 * 60 * 60 * 1000));
    }
  }

  const props: UserProperties = {
    ...(tipo_usuario && { tipo_usuario }),
    ...(dias_desde_cadastro !== undefined && { dias_desde_cadastro }),
    ...extras,
  };
  if (Object.keys(props).length > 0) setUserProperties(props);
}

// =============================================================================
// Tipos dos parametros de cada evento
// =============================================================================

export interface ParamsCadastroIniciado {
  /** Metodo de cadastro: 'email' | 'google' | 'apple' */
  metodo: string;
  /** Pagina ou tela de origem */
  origem?: string;
}

export interface ParamsCadastroConcluido {
  /** Metodo de cadastro: 'email' | 'google' | 'apple' */
  metodo: string;
  /** Plano selecionado: 'solo' | 'pro' | 'escala' */
  plano?: string;
  /** Dados do usuario para enhanced match (Meta) */
  usuario?: UserMetaInput;
  /**
   * O `event_id` que a API devolveu ao criar a conta (`cadastro_<usuario>`).
   *
   * O mesmo cadastro sai tambem do servidor, por CAPI: com o id igual nos dois
   * lados o Meta conta uma conversao so. Quem esta logado nao precisa passar
   * (o id sai do proprio usuario); quem cadastra SEM sessao, como o checkout
   * publico, so tem esse caminho.
   */
  event_id?: string;
}

export interface ParamsLogin {
  /** Metodo de login: 'email' | 'google' | 'apple' */
  metodo: string;
}

export interface ParamsTrialIniciado {
  plano: string;
  duracao_dias?: number;
  usuario?: UserMetaInput;
}

export interface ParamsPlanoGratisIniciado {
  plano: string;
  usuario?: UserMetaInput;
}

export interface ParamsLeadQualificado {
  /** Dias entre cadastro e completar os 3 marcos (>= 0) */
  dias_ate_qualificacao: number;
  /**
   * Qual dos 3 marcos foi o ultimo a ser completado. Corrida e musculacao
   * qualificam igual, mas viajam com rotulo proprio para dar pra separar o
   * personal de corrida na campanha.
   */
  gatilho: 'primeiro_aluno' | 'primeiro_treino' | 'primeiro_treino_corrida';
  /** Dados do usuario para enhanced match (Meta) */
  usuario?: UserMetaInput;
  /**
   * event_id que a API gerou ao qualificar (`lead_qualificado_<personal>`). Ela
   * manda o evento por CAPI; este id compartilhado faz o Meta deduplicar se o
   * navegador mandar também.
   */
  event_id?: string;
}

// --- Funil de checkout ---

export interface ParamsViewItem {
  /** Plano visualizado: 'solo' | 'pro' | 'escala' */
  plano: string;
  valor: number;
  periodo?: string;
  moeda?: string;
}

export interface ParamsSelectContent {
  /** Plano clicado */
  plano: string;
  valor?: number;
  periodo?: string;
}

export interface ParamsBeginCheckout {
  plano: string;
  valor: number;
  periodo?: string;
  moeda?: string;
  usuario?: UserMetaInput;
  event_id?: string;
}

export interface ParamsAddPaymentInfo {
  /** Metodo de pagamento escolhido: 'pix' | 'cartao' (boleto nao existe no app) */
  metodo_pagamento: string;
  plano: string;
  valor: number;
  periodo?: string;
  moeda?: string;
}

export interface ParamsAssinaturaPaga {
  /** ID da transacao do gateway (Asaas). OBRIGATORIO para GA4 registrar receita */
  transaction_id: string;
  plano: string;
  valor: number;
  moeda?: string;
  metodo_pagamento?: string;
  periodo?: string;
  usuario?: UserMetaInput;
  /**
   * event_id Meta CAPI compartilhado com o servidor para deduplicação
   * client+server. Quando o backend (webhook do Asaas) dispara o Purchase via
   * CAPI, ele salva o `meta_purchase_event_id` na assinatura, o frontend lê
   * esse id aqui e usa o MESMO no `dataLayer.push`. Sem ele, gera-se um UUID
   * novo (compatível com o comportamento anterior).
   */
  event_id?: string;
}

export interface ParamsIrParaOApp {
  /** De qual confirmacao a pessoa saiu: 'cadastro' | 'pagamento' */
  origem: 'cadastro' | 'pagamento';
  /** Para onde o botao levou, ja resolvido pela tela */
  destino: string;
}

export interface ParamsRefund {
  /** ID da transacao a ser reembolsada (mesmo transaction_id do assinaturaPaga) */
  transaction_id: string;
  valor: number;
  moeda?: string;
  motivo?: string;
}

export interface ParamsAssinaturaCancelada {
  plano: string;
  motivo?: string;
  dias_ativo?: number;
}

// =============================================================================
// Funcao auxiliar, obter dados do usuario logado para enhanced match
// =============================================================================

export function obterUsuarioParaMeta(): UserMetaInput | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const userStr = localStorage.getItem('welltrainer_user');
    if (!userStr) return undefined;
    const u = JSON.parse(userStr);
    return {
      email: u.email,
      phone: u.phoneNumber,
      firstName: u.firstName,
      lastName: u.lastName,
      id: u.id,
      birthday: u.birthday,
      country: 'br',
    };
  } catch {
    return undefined;
  }
}

// =============================================================================
// Funcoes auxiliares
// =============================================================================

function pushEvento(nomeEvento: string, params: Record<string, unknown> = {}): void {
  if (typeof window === 'undefined') return;
  /*
   * NENHUM evento sai de um aparelho com conta de ALUNO aberta.
   *
   * Esta é a terceira e última barreira, e a única que funciona no caso que
   * escapava de todas: numa SPA o GTM carrega na tela de entrar, quando ainda
   * não há login, e continua de pé depois que um aluno entra. A partir dali
   * qualquer `dataLayer.push` chegava ao Meta e ao GA4 com um aluno do outro
   * lado. As outras duas barreiras (rota e carregamento dos scripts) vivem em
   * `lib/utils/tracking-routes.ts`.
   */
  if (ehAlunoNesteAparelho()) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: nomeEvento,
    ...params,
  });
}

/**
 * Constroi user_data tolerando falha do crypto.subtle (WebView antigo, contexto
 * inseguro). Se hashar fica indisponível, o evento ainda vai pro dataLayer sem
 * enhanced match, perde dedupe Meta CAPI mas não some do GA4.
 */
async function buildUserDataSafe(usuario?: UserMetaInput) {
  if (!usuario) return undefined;
  try {
    return await buildUserDataForMeta(usuario);
  } catch (err) {
    console.warn('[tracking] buildUserDataForMeta falhou, segue sem user_data', err);
    return undefined;
  }
}

/**
 * Dispara um evento no pixel do ChatGPT (OpenAI). O pixel NÃO passa pelo GTM —
 * o snippet fica em `wt-tracking-scripts.tsx`, que já respeita os gates de rota
 * externa do personal / área logada e não carrega em desenvolvimento. Aqui a
 * chamada é best-effort: se `window.oaiq` não existir (rota sem os scripts),
 * apenas não dispara.
 */
function medirPixelOpenAI(evento: string, params: Record<string, unknown> = {}): void {
  if (typeof window === 'undefined') return;
  /* Mesmo corte do `pushEvento`: este pixel não passa pelo GTM, então precisa da
     sua própria pergunta. */
  if (ehAlunoNesteAparelho()) return;
  try {
    window.oaiq?.('measure', evento, params);
  } catch (err) {
    console.warn('[tracking] pixel OpenAI falhou', err);
  }
}

/**
 * O mesmo marco para o SDK da Meta do app instalado (a casca Expo, `webview/`).
 *
 * Dentro do app o pixel não enxerga a pessoa (a WebView tem cookie próprio e o
 * GTM nem carrega no painel), e o SDK não enxerga a página: sem esta ponte, a
 * campanha de app via só instalação e abertura. Os nomes são os padrão da Meta
 * para APP (`fb_mobile_*`), que não são os do pixel. No navegador não faz nada.
 *
 * A abertura do app não passa por aqui: o próprio SDK manda
 * `fb_mobile_activate_app`, e mandar daqui também contaria duas vezes.
 */
function medirNoAppDaMeta(dados: EventoDaMetaNoApp): void {
  if (typeof window === 'undefined') return;
  /* Mesmo corte do `pushEvento`: aluno não é o público das campanhas. */
  if (ehAlunoNesteAparelho()) return;
  try {
    avisarEventoDaMeta(dados);
  } catch (err) {
    console.warn('[tracking] evento do app da Meta falhou', err);
  }
}

function gerarEventId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback simples para ambientes sem crypto.randomUUID
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function hojeEmBrasilia(): string {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Sao_Paulo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

// =============================================================================
// Objeto exportado com todos os metodos de rastreamento
// =============================================================================

export const rastrear = {
  /**
   * Usuario iniciou o formulario de cadastro.
   * GA4: custom | Meta: InitiateCheckout
   */
  cadastroIniciado(params: ParamsCadastroIniciado): void {
    pushEvento('cadastro_iniciado', {
      event_id: gerarEventId(),
      metodo_cadastro: params.metodo,
      origem: params.origem || window.location.pathname,
    });
  },

  /**
   * Cadastro finalizado com sucesso.
   * GA4: sign_up | Meta: **Lead**
   *
   * Conferido no container GTM-N4GFPF48 ao vivo em 15/09/2026: a tag deste
   * evento faz `fbq("track","Lead",{},{eventID})` nos dois pixels (BR
   * 1231086455843649 e PT 1619306659157304). Dizia `CompleteRegistration` aqui,
   * e isso importa: o servidor manda `Lead` com o MESMO `event_id`, e o Meta só
   * deduplica quando nome E id batem. Com os nomes divergindo no papel, a
   * primeira leitura de quem revisa é que há conversão dobrada onde não há.
   *
   * Idempotente por usuário, se já foi disparado para esse `usuario.id` neste
   * browser, pula. Evita double-fire entre fluxos Google/Apple (que disparam no
   * useAuth) e a tela `/completar-cadastro` que possa rodar depois.
   */
  async cadastroConcluido(params: ParamsCadastroConcluido): Promise<void> {
    const dedupeKey = params.usuario?.id || params.usuario?.email;
    if (typeof window !== 'undefined' && dedupeKey) {
      const flagKey = `welltrainer_signup_tracked_${dedupeKey}`;
      try {
        if (localStorage.getItem(flagKey)) return;
        localStorage.setItem(flagKey, String(Date.now()));
      } catch {
        // localStorage indisponível, segue sem dedupe (pior caso: 2 eventos
        // num refresh raro vs perder o evento completo).
      }
    }

    /* O MESMO id que o servidor usa no `Lead` desta conta.
       O cadastro dispara dos dois lados: aqui (para o pixel, via GTM) e na API
       (por CAPI, que é o que sobrevive a bloqueador e a app recém-instalado).
       Com o id igual nos dois, o Meta conta uma conversão só. Sem o id da conta
       em mãos, cai no UUID de sempre. */
    const event_id =
      params.event_id || (params.usuario?.id ? `cadastro_${params.usuario.id}` : gerarEventId());
    const user_data = await buildUserDataSafe(params.usuario);
    const gclid = getStoredGclid();

    pushEvento('cadastro_concluido', {
      event_id,
      ...(user_data && { user_data }),
      ...(gclid && { gclid }),
      metodo_cadastro: params.metodo,
      plano: params.plano || 'gratuito',
    });

    // Pixel do ChatGPT (OpenAI) — cadastro é ação sem receita, por isso amount 0.
    // Protegido pelo mesmo dedupe por usuário do bloco acima.
    medirPixelOpenAI('registration_completed', {
      type: 'customer_action',
      amount: 0,
      currency: 'USD',
    });

    // App da Meta: mesmo dedupe por usuário do topo, então sai uma vez por conta.
    medirNoAppDaMeta({
      evento: 'fb_mobile_complete_registration',
      parametros: { fb_registration_method: params.metodo },
    });
  },

  /**
   * Login bem-sucedido.
   * GA4: login | Meta:
   */
  login(params: ParamsLogin): void {
    pushEvento('login', {
      event_id: gerarEventId(),
      metodo_login: params.metodo,
    });
  },

  /**
   * Primeiro aluno cadastrado pelo personal trainer.
   * GA4: custom | Meta: AddToCart
   */
  primeiroAluno(): void {
    pushEvento('primeiro_aluno', { event_id: gerarEventId() });
  },

  /**
   * Primeiro treino criado na plataforma.
   * GA4: custom | Meta:
   */
  primeiroTreino(): void {
    pushEvento('primeiro_treino', { event_id: gerarEventId() });
  },

  /**
   * Trial gratuito iniciado.
   * GA4: generate_lead | Meta: Lead
   */
  async trialIniciado(params: ParamsTrialIniciado): Promise<void> {
    const event_id = gerarEventId();
    const user_data = await buildUserDataSafe(params.usuario);
    const gclid = getStoredGclid();

    pushEvento('trial_iniciado', {
      event_id,
      ...(user_data && { user_data }),
      ...(gclid && { gclid }),
      plano: params.plano,
      duracao_dias: params.duracao_dias || 7,
    });

    // Pixel do ChatGPT (OpenAI) — equivalente ao Lead do Meta / generate_lead do GA4.
    medirPixelOpenAI('lead_created', { type: 'customer_action' });
  },

  /**
   * Plano grátis iniciado.
   * GA4: generate_lead | Meta: Lead
   *
   * Idempotente por usuário (mesmo motivo do cadastroConcluido).
   */
  async planoGratisIniciado(params: ParamsPlanoGratisIniciado): Promise<void> {
    const dedupeKey = params.usuario?.id || params.usuario?.email;
    if (typeof window !== 'undefined' && dedupeKey) {
      const flagKey = `welltrainer_planogratis_tracked_${dedupeKey}`;
      try {
        if (localStorage.getItem(flagKey)) return;
        localStorage.setItem(flagKey, String(Date.now()));
      } catch {}
    }

    const event_id = gerarEventId();
    const user_data = await buildUserDataSafe(params.usuario);
    const gclid = getStoredGclid();

    pushEvento('plano_gratis_iniciado', {
      event_id,
      ...(user_data && { user_data }),
      ...(gclid && { gclid }),
      plano: params.plano,
    });

    // Pixel do ChatGPT (OpenAI) — equivalente ao Lead do Meta / generate_lead do GA4.
    // Protegido pelo mesmo dedupe por usuário do bloco acima.
    medirPixelOpenAI('lead_created', { type: 'customer_action' });
  },

  /**
   * Lead qualificado, personal completou os 3 marcos (cadastro + 1+ aluno + 1+ treino).
   * Disparado uma unica vez por usuario (decisao no backend).
   * GA4: custom | Meta: LeadQualificado (Custom Event)
   */
  async leadQualificado(params: ParamsLeadQualificado): Promise<void> {
    /* O MESMO id do evento que o servidor mandou.
       Quem manda este evento de verdade é a API (`LeadQualificacaoService`), por
       CAPI, porque ele acontece dentro do painel e ali o GTM não carrega. Este
       disparo é o do navegador, para o caso de a rota ter GTM; com o id do
       servidor, o Meta conta uma conversão só. */
    const event_id = params.event_id || gerarEventId();
    const usuarioParaMeta = params.usuario ?? obterUsuarioParaMeta();
    const user_data = await buildUserDataSafe(usuarioParaMeta);
    const gclid = getStoredGclid();

    pushEvento('lead_qualificado', {
      event_id,
      ...(user_data && { user_data }),
      ...(gclid && { gclid }),
      dias_ate_qualificacao: params.dias_ate_qualificacao,
      gatilho: params.gatilho,
    });
  },

  /**
   * Usuario visualiza uma opcao de plano.
   * GA4: view_item | Meta: ViewContent
   */
  viewItem(params: ParamsViewItem): void {
    pushEvento('view_item', {
      event_id: gerarEventId(),
      currency: params.moeda || 'BRL',
      value: params.valor,
      items: [{
        item_id: params.plano,
        item_name: params.plano,
        item_category: 'assinatura',
        item_variant: params.periodo,
        price: params.valor,
        quantity: 1,
      }],
      plano: params.plano,
      periodo: params.periodo,
    });
  },

  /**
   * Usuario seleciona um plano especifico.
   * GA4: select_content / select_item | Meta:
   */
  selectContent(params: ParamsSelectContent): void {
    pushEvento('select_content', {
      event_id: gerarEventId(),
      content_type: 'plano',
      item_id: params.plano,
      plano: params.plano,
      ...(params.valor !== undefined && { value: params.valor, currency: 'BRL' }),
      ...(params.periodo && { periodo: params.periodo }),
    });
  },

  /**
   * Usuario entra no fluxo de checkout (tela de assinatura ou pagina de pagamento).
   * GA4: begin_checkout | Meta: InitiateCheckout
   *
   * Compartilha o `event_id` determinístico do dia (`initiate_checkout_<usuario>_<YYYY-MM-DD>`)
   * com o envio CAPI de `oQueEuUso` na API, e deduplica no aparelho para não contar duas
   * vezes quando a pessoa abre `/dashboard/personal/assinatura` e avança para `/pagamento`.
   */
  async beginCheckout(params: ParamsBeginCheckout): Promise<void> {
    const usuarioParaMeta = params.usuario ?? obterUsuarioParaMeta();
    const event_id =
      params.event_id ||
      (usuarioParaMeta?.id
        ? `initiate_checkout_${usuarioParaMeta.id}_${hojeEmBrasilia()}`
        : gerarEventId());

    if (typeof window !== 'undefined' && usuarioParaMeta?.id) {
      const flagKey = `welltrainer_begin_checkout_${event_id}`;
      try {
        if (localStorage.getItem(flagKey)) return;
        localStorage.setItem(flagKey, String(Date.now()));
      } catch {
        // localStorage indisponível, segue com a deduplicação pelo event_id no Meta.
      }
    }

    const user_data = await buildUserDataSafe(usuarioParaMeta);

    pushEvento('begin_checkout', {
      event_id,
      ...(user_data && { user_data }),
      currency: params.moeda || 'BRL',
      value: params.valor,
      items: [{
        item_id: params.plano,
        item_name: params.plano,
        item_category: 'assinatura',
        item_variant: params.periodo,
        price: params.valor,
        quantity: 1,
      }],
      plano: params.plano,
      periodo: params.periodo,
    });

    medirNoAppDaMeta({
      evento: 'fb_mobile_initiated_checkout',
      valor: params.valor,
      parametros: {
        fb_currency: params.moeda || 'BRL',
        fb_content_type: 'product',
        fb_content_id: params.plano,
        fb_num_items: 1,
        ...(params.periodo && { periodo: params.periodo }),
      },
    });
  },

  /**
   * Usuario escolhe/preenche metodo de pagamento.
   * GA4: add_payment_info | Meta: AddPaymentInfo
   */
  addPaymentInfo(params: ParamsAddPaymentInfo): void {
    pushEvento('add_payment_info', {
      event_id: gerarEventId(),
      currency: params.moeda || 'BRL',
      value: params.valor,
      payment_type: params.metodo_pagamento,
      items: [{
        item_id: params.plano,
        item_name: params.plano,
        item_category: 'assinatura',
        item_variant: params.periodo,
        price: params.valor,
        quantity: 1,
      }],
      plano: params.plano,
      metodo_pagamento: params.metodo_pagamento,
      periodo: params.periodo,
    });

    /* Sai a cada troca de forma de pagamento, como o pixel acima: o app e o site
       contam a mesma coisa, e é isso que deixa os dois números comparáveis. */
    medirNoAppDaMeta({
      evento: 'fb_mobile_add_payment_info',
      valor: params.valor,
      parametros: {
        fb_currency: params.moeda || 'BRL',
        fb_content_type: 'product',
        fb_content_id: params.plano,
        metodo_pagamento: params.metodo_pagamento,
        ...(params.periodo && { periodo: params.periodo }),
      },
    });
  },

  /**
   * Pagamento confirmado (conversao principal).
   * GA4: purchase | Meta: Purchase
   *
   * transaction_id e OBRIGATORIO: sem ele o GA4 nao registra receita, e e ele
   * que faz o GA4 nao contar a mesma venda duas vezes.
   *
   * ELE NAO E "O ID DO ASAAS". Era, quando o Asaas era o unico gateway. Hoje o
   * Pix passa pela Woovi, que nao gera `asaas_payment_id`, e quem monta a URL da
   * confirmacao usa `asaas_payment_id || assinatura.id`: o que importa e ser
   * ESTAVEL e o MESMO em toda tentativa daquela compra, e o id da assinatura e
   * as duas coisas. Se um dia isso virar o id da cobranca da Woovi, a troca tem
   * que ser de uma vez, senao a mesma venda entra no GA4 com dois ids.
   *
   * O Purchase NAO depende desta funcao para existir: ele sai do servidor
   * (`meta-capi.ts` no pagamento confirmado, e o daemon wt-capi) com o event_id
   * `purchase_{assinatura_id}`, que e o mesmo que chega aqui em `event_id`. E
   * isso que deixa a Meta contar uma venda so entre navegador e servidor. Quem
   * paga por Pix e nao volta pro site continua sendo contado.
   *
   * Apenas dataLayer.push: o GTM cuida de disparar Meta Pixel, GA4 e Google
   * Ads. Nao fazer double push.
   */
  async assinaturaPaga(params: ParamsAssinaturaPaga): Promise<void> {
    if (!params.transaction_id) {
      console.warn('[rastrear.assinaturaPaga] transaction_id obrigatorio, evento ignorado');
      return;
    }

    // Idempotente por compra neste browser (mesmo padrão do cadastroConcluido):
    // refresh, revisita ao /bem-vindo ou re-render do polling não podem
    // re-disparar Purchase. Chave preferencial = event_id do backend
    // (purchase_{assinatura_id}, estável); fallback = transaction_id.
    const dedupeKey = params.event_id || params.transaction_id;
    if (typeof window !== 'undefined' && dedupeKey) {
      const flagKey = `welltrainer_purchase_tracked_${dedupeKey}`;
      try {
        if (localStorage.getItem(flagKey)) return;
        localStorage.setItem(flagKey, String(Date.now()));
      } catch {
        // localStorage indisponível, segue sem dedupe local (o event_id
        // compartilhado com o server ainda dedupa na Meta).
      }
    }

    // Preferir o event_id vindo do backend (CAPI deduplicação). Fallback p/ UUID
    // novo se o webhook ainda não tiver salvo (race condition no polling).
    const event_id = params.event_id || gerarEventId();
    const user_data = await buildUserDataSafe(params.usuario);
    const gclid = getStoredGclid();
    const moeda = params.moeda || 'BRL';

    pushEvento('assinatura_paga', {
      event_id,
      transaction_id: params.transaction_id,
      ...(user_data && { user_data }),
      ...(gclid && { gclid }),
      currency: moeda,
      value: params.valor,
      items: [{
        item_id: params.plano,
        item_name: params.plano,
        item_category: 'assinatura',
        item_variant: params.periodo,
        price: params.valor,
        quantity: 1,
      }],
      plano: params.plano,
      valor: params.valor,
      moeda,
      metodo_pagamento: params.metodo_pagamento,
      periodo: params.periodo,
    });

    /* A compra no app da Meta, com valor e moeda (sem os dois a casca descarta).
       Passa pelo mesmo dedupe por compra do topo: refresh e polling não repetem.
       Quem paga por Pix e fecha o app antes da confirmação não chega a esta
       tela nem gera o evento do app; a venda continua contada pelo servidor,
       no pixel. */
    medirNoAppDaMeta({
      evento: 'fb_mobile_purchase',
      valor: params.valor,
      moeda,
      parametros: {
        fb_content_type: 'product',
        fb_content_id: params.plano,
        fb_num_items: 1,
        fb_order_id: params.transaction_id,
        ...(params.metodo_pagamento && { metodo_pagamento: params.metodo_pagamento }),
        ...(params.periodo && { periodo: params.periodo }),
      },
    });
  },

  /**
   * Clique no "Ir para o app" das paginas de conclusao (`/cadastro-concluido` e
   * `/pagamento-concluido`).
   * GA4: custom
   *
   * E o ultimo ponto medido antes de a pessoa entrar na area logada, onde o GTM
   * deixa de carregar de proposito: depois daqui o funil de marketing termina.
   * Nao e idempotente, quem clica duas vezes conta duas, que e a informacao que
   * se quer ter (o botao nao levou a lugar nenhum na primeira).
   */
  irParaOApp(params: ParamsIrParaOApp): void {
    pushEvento('ir_para_o_app', {
      event_id: gerarEventId(),
      origem: params.origem,
      destino: params.destino,
    });
  },

  /**
   * Reembolso de pagamento.
   * GA4: refund | Meta:
   */
  refund(params: ParamsRefund): void {
    pushEvento('refund', {
      event_id: gerarEventId(),
      transaction_id: params.transaction_id,
      currency: params.moeda || 'BRL',
      value: params.valor,
      motivo: params.motivo,
    });
  },

  /**
   * Assinatura cancelada (churn tracking).
   * GA4: custom | Meta:
   */
  assinaturaCancelada(params: ParamsAssinaturaCancelada): void {
    pushEvento('assinatura_cancelada', {
      event_id: gerarEventId(),
      plano: params.plano,
      motivo: params.motivo,
      dias_ativo: params.dias_ativo,
    });
  },

  /**
   * Visualizacao de pagina (para SPAs, chamar em cada navegacao de rota).
   * GA4: page_view | Meta: PageView
   *
   * @deprecated Use initRouteTracker() no layout raiz com dep [pathname]
   */
  paginaVista(titulo?: string): void {
    pushEvento('pagina_vista', {
      event_id: gerarEventId(),
      pagina: window.location.pathname,
      titulo_pagina: titulo || document.title,
      url_completa: window.location.href,
    });
  },
};

/**
 * Dispara o `lead_qualificado` a partir da RESPOSTA de qualquer endpoint que
 * possa ter qualificado a conta (cadastrar aluno, montar treino, montar ou
 * aplicar plano de corrida). Se a resposta nao traz `lead_qualificado`, nao
 * acontece nada, que e o caso comum: qualificar acontece uma vez na vida.
 *
 * Existe como peca unica porque eram tres copias da mesma checagem espalhadas
 * pelos services, e a de corrida simplesmente nao existia: o personal de
 * corrida nunca qualificava. Endpoint novo que possa fechar o marco chama esta
 * funcao com a resposta crua, e nada mais.
 */
export function dispararLeadQualificadoSeExiste(payload: unknown): void {
  if (!payload || typeof payload !== 'object') return;
  const lq = (payload as { lead_qualificado?: ParamsLeadQualificado & { qualificado?: true } })
    .lead_qualificado;
  if (!lq || lq.qualificado !== true) return;
  void rastrear.leadQualificado({
    dias_ate_qualificacao: lq.dias_ate_qualificacao,
    gatilho: lq.gatilho,
    usuario: obterUsuarioParaMeta(),
    event_id: lq.event_id,
  });
}

export default rastrear;
